// v2/components/QuoteParts.js — Teklif ekranlarinin ortak parcalari (musteri + yonetim)
import React, { useEffect, useRef, useState } from 'react';
import {
  ChevronDown, Loader2, AlertTriangle, Repeat, CheckCircle, Package,
} from 'lucide-react';
import ProductThumb from './ProductThumb';
import { DiscountChips } from './ProductRow';
import {
  LINE_STATUS, LINE_STATUS_ORDER, MATCH_LABEL, WARNING_LABEL, karsilamaOrani, fmtEur,
} from '../hooks/useQuotes';
import { formatTRY, formatNumber } from '../utils/format';
import css from '../styles/QuotesV2.module.css';

export const AKTARILABILIR = ['tam', 'kismi'];

export function Acilir({ etiket, Icon, secenekler, onSec, mesgul }) {
  const [acik, setAcik] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!acik) return undefined;
    const kapat = (e) => { if (ref.current && !ref.current.contains(e.target)) setAcik(false); };
    document.addEventListener('mousedown', kapat);
    return () => document.removeEventListener('mousedown', kapat);
  }, [acik]);
  return (
    <span className={css.ddWrap} ref={ref}>
      <button type="button" className={css.btnGhost} onClick={() => setAcik((a) => !a)}
              disabled={mesgul} aria-expanded={acik}>
        {mesgul ? <Loader2 size={14} className={css.spin} /> : <Icon size={14} />}
        {etiket}<ChevronDown size={13} />
      </button>
      {acik && (
        <span className={css.ddMenu} role="menu">
          {secenekler.map((s) => (
            <button key={s.id} type="button" role="menuitem" className={css.ddItem}
                    onClick={() => { setAcik(false); onSec(s.id); }}>
              <span className={css.ddLabel}>{s.label}</span>
              {s.hint && <span className={css.ddHint}>{s.hint}</span>}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

/** TL (ust) + Euro (alt) */
export function Para({ tl, eur, sinif }) {
  if (tl === null || tl === undefined) return <span className={css.muted}>—</span>;
  return (
    <span className={css.priceCell}>
      <span className={sinif}>{formatTRY(tl)}</span>
      {eur !== null && eur !== undefined && <span className={css.eur}>{fmtEur(eur)}</span>}
    </span>
  );
}

export function OzetKartlari({ q, aktif, onSec }) {
  const c = q.counts || {};
  const kartlar = [
    { id: 'all', label: 'Toplam Kalem', n: q.lineCount, tone: 'kNavy' },
    ...LINE_STATUS_ORDER.map((d) => ({ id: d, label: LINE_STATUS[d].label, n: c[d] || 0,
                                       tone: LINE_STATUS[d].tone })),
  ];
  if (c.kalkti) kartlar.push({ id: 'kalkti', label: 'Üretimden Kalkan', n: c.kalkti, tone: 'tPurple' });
  return (
    <div className={css.kpiGrid}>
      {kartlar.map((k) => (
        <button key={k.id} type="button" onClick={() => onSec(k.id)}
                className={`${css.kpi} ${css[k.tone]} ${aktif === k.id ? css.kpiOn : ''}`}
                aria-pressed={aktif === k.id}>
          <span className={css.kpiValue}>{formatNumber(k.n)}</span>
          <span className={css.kpiLabel}>{k.label}</span>
        </button>
      ))}
    </div>
  );
}

export function Tutarlar({ q }) {
  const t = q.totals || {};
  const e = q.totalsEur || {};
  const indirim = Math.max((t.gross || 0) - (t.net || 0), 0);
  const kdv = Math.max((t.withVat || 0) - (t.net || 0), 0);
  const indirimE = Math.max((e.gross || 0) - (e.net || 0), 0);
  const kdvE = Math.max((e.withVat || 0) - (e.net || 0), 0);
  const oran = karsilamaOrani(q);
  return (
    <div className={css.sumGrid}>
      <section className={css.card}>
        <div className={css.cardHead}>
          <span className={css.cardBar} />
          <h2 className={css.cardTitle}>Karşılama Özeti</h2>
        </div>
        <div className={css.sumRows}>
          <div className={css.sumRow}><span>Talep edilen toplam adet</span><b>{formatNumber(q.requestedQty)}</b></div>
          <div className={css.sumRow}><span>Karşılanabilen toplam adet</span><b>{formatNumber(q.suppliedQty)}</b></div>
          <div className={css.sumRow}><span>Talebin tamamı (fiyatlı kalemler, net)</span><span className={css.sumVals}><b>{formatTRY(t.requestedNet)}</b><span className={css.eur}>{fmtEur(e.requestedNet)}</span></span></div>
        </div>
        <div className={css.bigRate}>
          <span className={css.rateTrack}><span className={css.rateFill} style={{ width: `${oran}%` }} /></span>
          <span className={css.bigRateText}>%{formatNumber(oran, 1)} karşılanabilir</span>
        </div>
      </section>
      <section className={css.card}>
        <div className={css.cardHead}>
          <span className={css.cardBar} />
          <h2 className={css.cardTitle}>Teklif Tutarı</h2>
          <span className={css.cardChip}>karşılanabilen miktar</span>
        </div>
        <div className={css.sumRows}>
          <div className={css.sumRow}><span>Brüt Toplam</span><span className={css.sumVals}><span>{formatTRY(t.gross)}</span><span className={css.eur}>{fmtEur(e.gross)}</span></span></div>
          {indirim > 0 && <div className={css.sumRow}><span>İndirim</span><span className={css.sumVals}><span className={css.green}>−{formatTRY(indirim)}</span><span className={css.eur}>−{fmtEur(indirimE)}</span></span></div>}
          <div className={css.sumRow}><span>Net Toplam</span><span className={css.sumVals}><b>{formatTRY(t.net)}</b><span className={css.eur}>{fmtEur(e.net)}</span></span></div>
          <div className={css.sumRow}><span>KDV (%{formatNumber(q.vatRate || 20)})</span><span className={css.sumVals}><span>{formatTRY(kdv)}</span><span className={css.eur}>{fmtEur(kdvE)}</span></span></div>
        </div>
        <div className={css.grand}>
          <span>Genel Toplam (KDV Dahil)</span>
          <span className={css.grandVals}><b>{formatTRY(t.withVat)}</b><span className={css.grandEur}>{fmtEur(e.withVat)}</span></span>
        </div>
      </section>
    </div>
  );
}

export function Uyarilar({ q }) {
  const [acik, setAcik] = useState(false);
  const sayac = (q.counts && q.counts.uyari) || {};
  const toplam = Object.values(sayac).reduce((a, b) => a + b, 0);
  if (!toplam) return null;
  const liste = q.warnings || [];
  return (
    <div className={css.warnBox}>
      <button type="button" className={css.warnHead} onClick={() => setAcik((a) => !a)} aria-expanded={acik}>
        <AlertTriangle size={15} />
        <span>Dosyanızda {formatNumber(toplam)} satır için düzeltme yapıldı</span>
        <span className={css.warnChips}>
          {Object.entries(sayac).map(([k, n]) => (
            <span key={k} className={css.warnChip}>{WARNING_LABEL[k] || k}: {formatNumber(n)}</span>
          ))}
        </span>
        <ChevronDown size={14} className={acik ? css.rot : undefined} />
      </button>
      {acik && (
        <div className={css.warnList}>
          {liste.map((w, i) => (
            <span key={`${w.row}-${i}`} className={css.warnItem}>
              <b>Satır {w.row}</b>{w.code && <code>{w.code}</code>}{w.message}
            </span>
          ))}
          {liste.length < toplam && (
            <span className={css.muted}>İlk {liste.length} uyarı gösteriliyor.</span>
          )}
        </div>
      )}
    </div>
  );
}

export function Depolar({ l }) {
  if (!l.code) return <span className={css.muted}>—</span>;
  return (
    <span className={css.whTags}>
      <span className={`${css.whTag} ${l.gzt ? css.whOn : css.whOff}`} title={l.gzt ? 'Gaziantep: Mevcut' : 'Gaziantep: Stok Yok'}>GZT</span>
      <span className={`${css.whTag} ${l.ist ? css.whOn : css.whOff}`} title={l.ist ? 'İstanbul: Mevcut' : 'İstanbul: Stok Yok'}>İST</span>
    </span>
  );
}

export function Karsilanan({ l }) {
  if (!AKTARILABILIR.includes(l.status)) return <span className={css.muted}>—</span>;
  const oran = l.qty ? (l.supplied * 100) / l.qty : 0;
  return (
    <span className={css.suppCell}>
      <span className={css.suppText}>
        <b>{formatNumber(l.supplied)}</b><span className={css.muted}> / {formatNumber(l.qty)}</span>
      </span>
      {l.status === 'kismi' && (
        <span className={css.suppTrack}><span className={css.suppFill} style={{ width: `${oran}%` }} /></span>
      )}
    </span>
  );
}

/** Stok adetleri — YALNIZCA yonetim ekraninda */
export function StokAdet({ l }) {
  if (!l.code || !l.stock) return <span className={css.muted}>—</span>;
  const g = l.stock.gzt || 0;
  const i = l.stock.ist || 0;
  return (
    <span className={css.stockCell}>
      <span className={g > 0 ? css.stockOn : css.stockOff}>GZT <b>{formatNumber(g)}</b></span>
      <span className={i > 0 ? css.stockOn : css.stockOff}>İST <b>{formatNumber(i)}</b></span>
    </span>
  );
}

/**
 * Teklif kalem satiri (musteri + yonetim).
 * secimli: sepete aktarim icin onay kutusu sutunu (musteri)
 * yonetim: depo var/yok yerine canli stok adetleri
 */
export function KalemSatiri({
  l, secili = false, onSecim = () => {}, onCozumle, cozumleniyor, kilitli,
  secimli = true, yonetim = false,
}) {
  const st = LINE_STATUS[l.status] || LINE_STATUS.bulunamadi;
  const secilebilir = AKTARILABILIR.includes(l.status) && !l.transferred && !kilitli;
  const farkli = l.code && l.inputCode && l.inputCode.replace(/[^0-9A-Za-z]/g, '').toUpperCase()
    !== l.code.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  const eslesme = MATCH_LABEL[l.match];

  return (
    <tr className={`${css.lRow} ${l.transferred ? css.lDone : ''} ${secimli ? '' : css.noCheck}`}>
      {secimli && (
        <td className={css.cCheck}>
          <input type="checkbox" checked={secili} disabled={!secilebilir}
                 onChange={() => onSecim(l.id)} aria-label={`${l.code || l.inputCode} seç`} />
        </td>
      )}
      <td className={css.cThumb}>
        {l.code ? <ProductThumb code={l.code} name={l.name} className={css.thumb} imgClassName={css.thumbImg} />
                : <span className={css.thumbEmpty}><Package size={16} /></span>}
      </td>
      <td className={css.cCode}>
        {l.code ? <span className={css.code}>{l.code}</span> : <span className={css.codeMuted}>{l.inputCode}</span>}
        {farkli && <span className={css.subText} title="Dosyada yazılan kod">Girilen: {l.inputCode}</span>}
        {eslesme && <span className={css.matchTag}>{eslesme}</span>}
      </td>
      <td className={css.cName}>
        {l.status === 'bulunamadi' && <span className={css.muted}>Katalogda bulunamadı</span>}
        {l.status === 'kontrol' && (
          <span className={css.candBox}>
            <span className={css.candTitle}>Bu OEM numarası birden fazla ürünle eşleşti, birini seçin:</span>
            <span className={css.candList}>
              {l.candidates.map((k) => (
                <button key={k} type="button" className={css.candBtn} disabled={cozumleniyor || kilitli}
                        onClick={() => onCozumle(l, k)}>
                  {cozumleniyor === `${l.id}:${k}` ? <Loader2 size={12} className={css.spin} /> : null}{k}
                </button>
              ))}
            </span>
          </span>
        )}
        {l.status !== 'bulunamadi' && l.status !== 'kontrol' && (
          <span className={css.name} title={l.name}>{l.name}</span>
        )}
        {(l.customerCode || l.note) && (
          <span className={css.subText}>
            {l.customerCode && <>{yonetim ? 'Müşteri stok kodu' : 'Stok kodunuz'}: <b>{l.customerCode}</b></>}
            {l.customerCode && l.note && ' · '}
            {l.note}
          </span>
        )}
        {l.discontinued && (
          <span className={css.discRow}>
            <span className={`${css.pill} ${css.tPurple}`}>Üretimden kalktı</span>
            {l.successor && (
              <>
                <span className={css.muted}>İkame: <b>{l.successor}</b></span>
                {!l.transferred && !kilitli && (
                  <button type="button" className={css.linkBtn} disabled={Boolean(cozumleniyor)}
                          onClick={() => onCozumle(l, l.successor)}>
                    {cozumleniyor === `${l.id}:${l.successor}`
                      ? <Loader2 size={12} className={css.spin} /> : <Repeat size={12} />}
                    İkame ile değiştir
                  </button>
                )}
              </>
            )}
          </span>
        )}
      </td>
      <td className={css.right} data-label="Talep">{formatNumber(l.qty)}</td>
      <td data-label="Karşılanan"><Karsilanan l={l} /></td>
      <td data-label="Durum">
        <span className={`${css.pill} ${css[st.tone]}`}>{st.short}</span>
        {l.transferred && <span className={css.doneTag}><CheckCircle size={11} /> Sepette</span>}
      </td>
      <td data-label={yonetim ? 'Stok' : 'Depo'}>{yonetim ? <StokAdet l={l} /> : <Depolar l={l} />}</td>
      <td className={css.right} data-label="Birim Brüt"><Para tl={l.gross} eur={l.grossEur} sinif={css.gross} /></td>
      <td className={css.center} data-label="İndirim">
        {l.discounts && l.discounts.length
          ? <span className={css.chain}><DiscountChips discounts={l.discounts} /></span>
          : <span className={css.muted}>—</span>}
      </td>
      <td className={css.right} data-label="Birim Net"><Para tl={l.net} eur={l.netEur} sinif={css.net} /></td>
      <td className={css.right} data-label="Toplam Tutar">
        {l.lineNet ? <Para tl={l.lineNet} eur={l.lineNetEur} sinif={css.money} /> : <span className={css.muted}>—</span>}
      </td>
    </tr>
  );
}

